import useModal, { ModalProps } from '@carvana/ds-modal';
import React, { useContext, useEffect, useRef } from 'react';
import BackstopContext from '|/context/backstop-context';
import { useNavigationBlockContext } from '|/context/navigation-block-context';
import useAnalyticsContext from '|/hooks/use-analytics-context';
import useBrowserBackClose from '|/hooks/use-browser-back-close';
import { ModalEvents, ModalNames } from './modal-events';

type EventedModalProps = ModalProps & {
  analyticsId: ModalNames;
  children: React.ReactNode;
  isOpen: boolean;
  eventMetadata?: Record<string, any>;
  // ds-modal names the dialog from `modalHeader` (aria-labelledby) or `aria-label`, and its
  // ModalProps declares neither `aria-labelledby` nor any passthrough for it. A modal that already
  // renders its own visible title wants to point at that element rather than duplicate the string
  // in an aria-label that can drift, so declare it here — it lands in restProps and ds-modal
  // spreads those onto the dialog ahead of its own label props, which are undefined in that case.
  'aria-labelledby'?: string;
  // Modals that wire their own useBrowserBackClose (e.g. the embedded appraisal modal) opt out of
  // the experiment back-close guard so the key isn't registered twice. Presence is still tracked.
  selfManagesBackClose?: boolean;
};

const EventedModal = ({
  analyticsId,
  children,
  isOpen,
  eventMetadata,
  onClose,
  selfManagesBackClose = false,
  ...restProps
}: EventedModalProps) => {
  const { Modal, openModal, closeModal } = useModal();
  const { trackEvent } = useAnalyticsContext();
  const { isBackstopTreatment } = useContext(BackstopContext);
  const { addOpenModal, removeOpenModal } = useNavigationBlockContext();
  const prevIsOpenRef = useRef<boolean>(false);
  const trackEventRef = useRef(trackEvent);
  // Closed must name the modal Opened named. A modal may change analyticsId while open —
  // transfer-method derives it from a sub-view state it resets one render before isOpen flips — so
  // the live value at close time is the reset one, not the sub-view the user was actually looking at.
  const openedIdRef = useRef(analyticsId);

  useEffect(() => {
    const prevIsOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;

    if (isOpen && !prevIsOpen) {
      openedIdRef.current = analyticsId;
      openModal();
      trackEvent(ModalEvents.Opened, { modal: analyticsId, ...eventMetadata });
    } else if (!isOpen && prevIsOpen) {
      trackEvent(ModalEvents.Closed, { modal: openedIdRef.current });
      closeModal();
    }
  }, [isOpen, openModal, closeModal, trackEvent, analyticsId, eventMetadata]);

  useEffect(() => {
    trackEventRef.current = trackEvent;
  }, [trackEvent]);

  // Closed is emitted above only on an isOpen true -> false render. Consumers routinely tear the
  // modal down while it is still open instead — `{isOpen && <Modal isOpen={isOpen} />}` unmounts in
  // the same commit, verification-task-sheet passes a literal `isOpen`, and any navigation unmounts
  // the whole step subtree — so that render never happens and the close is lost (TXCC-9867).
  // prevIsOpenRef holds the last committed isOpen, so it is false after a normal close and this
  // cannot double-fire.
  useEffect(
    () => () => {
      if (!prevIsOpenRef.current) return;
      prevIsOpenRef.current = false;
      trackEventRef.current(ModalEvents.Closed, { modal: openedIdRef.current });
    },
    []
  );

  // Presence: every open modal registers, in both buckets and regardless of selfManagesBackClose,
  // so pp_browser_nav_button's modalOpen/modalName are accurate everywhere.
  useEffect(() => {
    if (!isOpen) return undefined;
    addOpenModal(analyticsId);
    return () => removeOpenModal(analyticsId);
  }, [isOpen, analyticsId, addOpenModal, removeOpenModal]);

  // Guard: arm the browser-Back close only in treatment for non-opted-out modals. Modals with no
  // onClose are deliberately non-dismissible — arming them would consume the user's Back on a
  // handler that does nothing, so the press is silently swallowed. The callback just closes; the
  // pp_browser_nav_button event is owned by the coordinator, not fired here.
  useBrowserBackClose(
    analyticsId,
    isOpen && isBackstopTreatment && !selfManagesBackClose && !!onClose,
    () => onClose?.()
  );

  return (
    <Modal onClose={onClose} {...restProps}>
      {children}
    </Modal>
  );
};

export default EventedModal;
